import { Container, Graphics } from "pixi.js";
import { createFeatheredEllipse } from "../lib/soft-shape";
import type { Point, Token, TwirlIntensity, TwirlStyle } from "../types";

const INTENSITY: Record<TwirlIntensity, { speed: number; arms: number; particles: number; opacity: number }> = {
  gentle: { speed: 0.54, arms: 3, particles: 12, opacity: 0.68 }, strong: { speed: 0.90, arms: 4, particles: 20, opacity: 0.82 }, violent: { speed: 1.38, arms: 5, particles: 30, opacity: 0.94 },
};
const ACCENTS: Record<TwirlStyle, { highlight: string; shadow: string }> = {
  dust: { highlight: "#e1bd84", shadow: "#4a3522" }, arcane: { highlight: "#e2d0ff", shadow: "#35205f" }, water: { highlight: "#c8f5ff", shadow: "#164d65" }, shadow: { highlight: "#aeb7ca", shadow: "#11131a" },
};
function seedFrom(value: string) { let hash = 2166136261; for (let i = 0; i < value.length; i += 1) hash = Math.imul(hash ^ value.charCodeAt(i), 16777619); return (hash >>> 0) / 4294967296; }

/** Animated top-down vortex made from open WebGL geometry, so no square filter boundary can show. */
export function createTwirlVortex(token: Token, position: Point, radius: number, alpha: number) {
  const twirl = new Container(); twirl.position.set(position.x, position.y); twirl.alpha = alpha;
  const settings = token.twirl ?? { style: "dust", intensity: "strong", direction: "clockwise" }; const intensity = INTENSITY[settings.intensity]; const accent = ACCENTS[settings.style];
  const direction = settings.direction === "clockwise" ? 1 : -1; const seed = seedFrom(token.id); const shadow = createFeatheredEllipse({ radiusX: radius * 1.38, radiusY: radius * 1.18, color: accent.shadow, alpha: settings.style === "shadow" ? 0.26 : 0.13, feather: 0.58 }); const spiral = new Graphics(); const particles = new Graphics();
  twirl.addChild(shadow, spiral, particles);
  let elapsed = seed * 8;
  const draw = () => {
    spiral.clear(); particles.clear();
    for (let arm = 0; arm < intensity.arms; arm += 1) {
      for (let band = 0; band < 3; band += 1) {
        const start = arm / intensity.arms * Math.PI * 2 + elapsed * intensity.speed * direction + band * 0.14;
        for (let step = 0; step <= 34; step += 1) {
          const progress = step / 34; const distance = radius * (0.10 + progress * (1.12 + band * 0.08));
          const angle = start + direction * (progress * 4.8 + Math.sin(progress * 9 + seed * 17 + band) * 0.10);
          const x = Math.cos(angle) * distance; const y = Math.sin(angle) * distance * 0.80;
          if (step === 0) spiral.moveTo(x, y); else spiral.lineTo(x, y);
        }
        spiral.stroke({ color: band === 1 ? accent.highlight : token.color, width: Math.max(1, radius * (0.10 - band * 0.022)), alpha: intensity.opacity * (0.34 - band * 0.065) });
      }
    }
    for (let index = 0; index < intensity.particles; index += 1) {
      const phase = seed * 23 + index * 2.399 + elapsed * intensity.speed * direction * (0.55 + index % 6 * 0.07); const orbit = radius * (0.18 + (index % 11) / 10 * 1.08);
      const size = Math.max(0.8, radius * (0.018 + index % 4 * 0.009));
      particles.circle(Math.cos(phase) * orbit, Math.sin(phase) * orbit * 0.78, size).fill({ color: index % 3 ? accent.highlight : token.color, alpha: 0.48 + index % 4 * 0.10 });
    }
  };
  draw();
  return { twirl, animate: (deltaMs: number) => { elapsed += Math.min(deltaMs, 80) / 1000; draw(); } };
}
